import { useEffect, useRef, useState } from "react";
import { apiPost } from "../api";
import { Icons } from "./icons";

const CATEGORY_PATTERN = /^[a-z0-9._-]+$/;

interface Props {
  value: string;
  categories: string[];
  /** Llamado cuando el usuario seleccionó/creó una categoría. */
  onChange: (next: string) => void;
  /** Llamado cuando se crea una categoría nueva en el server, para refrescar la lista. */
  onCreated?: (next: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}

export function CategoryCombobox({
  value,
  categories,
  onChange,
  onCreated,
  disabled,
  invalid,
}: Props) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const newInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        closeAll();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeAll();
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (creating && newInputRef.current) newInputRef.current.focus();
  }, [creating]);

  function closeAll() {
    setOpen(false);
    setCreating(false);
    setNewValue("");
    setError(null);
  }

  function selectCategory(c: string) {
    onChange(c);
    closeAll();
  }

  async function submitNew() {
    const normalized = newValue.trim().toLowerCase();
    if (!CATEGORY_PATTERN.test(normalized)) {
      setError("Solo lowercase, números, punto, guion y guion bajo.");
      return;
    }
    if (normalized.length > 40) {
      setError("Máximo 40 caracteres.");
      return;
    }
    if (categories.includes(normalized)) {
      // Ya existe → solo seleccionarla.
      selectCategory(normalized);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await apiPost<{ category: string }>("/v1/categories", {
        category: normalized,
      });
      onChange(normalized);
      onCreated?.(normalized);
      closeAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          ...(invalid
            ? { borderColor: "var(--st-major)", outlineColor: "var(--st-major)" }
            : {}),
        }}
      >
        <span style={{ color: value ? "var(--fg-1)" : "var(--fg-4)" }}>
          {value || "Elegí una categoría…"}
        </span>
        <Icons.ChevronDown size={12} sw={2} />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 300,
            background: "var(--bg-1)",
            border: "1px solid var(--border-1)",
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            maxHeight: 280,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ overflowY: "auto", padding: 4, flex: 1 }}>
            {categories.length === 0 && !creating && (
              <div
                style={{
                  padding: "10px 12px",
                  color: "var(--fg-3)",
                  fontSize: 12.5,
                  textAlign: "center",
                }}
              >
                Sin categorías todavía.
              </div>
            )}
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                role="option"
                aria-selected={c === value}
                onClick={() => selectCategory(c)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  background: c === value ? "var(--bg-2)" : "transparent",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  color: "var(--fg-1)",
                  fontSize: 13.5,
                  fontFamily: "var(--font-mono)",
                }}
                onMouseEnter={(e) => {
                  if (c !== value) e.currentTarget.style.background = "var(--bg-2)";
                }}
                onMouseLeave={(e) => {
                  if (c !== value) e.currentTarget.style.background = "transparent";
                }}
              >
                {c}
              </button>
            ))}
          </div>

          <div
            style={{
              borderTop: "1px solid var(--border-1)",
              padding: 6,
              background: "var(--bg-2)",
              borderBottomLeftRadius: 6,
              borderBottomRightRadius: 6,
            }}
          >
            {!creating ? (
              <button
                type="button"
                onClick={() => {
                  setCreating(true);
                  setNewValue("");
                  setError(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  width: "100%",
                  padding: "8px 10px",
                  background: "transparent",
                  border: "1px dashed var(--border-1)",
                  borderRadius: 4,
                  color: "var(--fg-2)",
                  fontSize: 12.5,
                  cursor: "pointer",
                }}
              >
                <Icons.Plus size={12} sw={2.2} />
                Nueva categoría
              </button>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    ref={newInputRef}
                    className="input"
                    placeholder="nombre-categoria"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value.toLowerCase())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void submitNew();
                      } else if (e.key === "Escape") {
                        setCreating(false);
                      }
                    }}
                    style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: 13 }}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!newValue || submitting}
                    onClick={submitNew}
                  >
                    {submitting ? "…" : "Crear"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setCreating(false);
                      setError(null);
                    }}
                    disabled={submitting}
                  >
                    Cancelar
                  </button>
                </div>
                {error && (
                  <div style={{ color: "var(--st-major)", fontSize: 11.5 }}>{error}</div>
                )}
                <div style={{ color: "var(--fg-4)", fontSize: 11.5 }}>
                  Lowercase, sin espacios. Queda disponible para todos los servicios.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
